import { collection, addDoc } from 'firebase/firestore'
import { db } from '@/firebase/config'
import { convertToFirestore } from './firestoreService'

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'
export type LogContext = {
  organizationId?: string
  userId?: string
  requestId?: string
  context?: Record<string, unknown>
}

const CONSOLE_ENABLED =
  import.meta.env.MODE !== 'production' || import.meta.env.VITE_LOG_CONSOLE === 'true'
const FS_ENABLED = 'true'

// Log level configuration
// Environment variables:
// - VITE_LOG_CONSOLE_LEVEL: 'debug' | 'info' | 'warn' | 'error' (default: debug in dev, info in prod)
// - VITE_LOG_DB_LEVEL: 'debug' | 'info' | 'warn' | 'error' (default: info)
const getLogLevel = (envVar: string | undefined, defaultLevel: number): number => {
  if (!envVar) return defaultLevel
  const levels = { debug: 0, info: 2, warn: 3, error: 4 }
  return levels[envVar as LogLevel] ?? defaultLevel
}

const CONSOLE_LOG_LEVEL = getLogLevel(
  import.meta.env.VITE_LOG_CONSOLE_LEVEL,
  import.meta.env.PROD ? 2 : 0,
)
const DB_LOG_LEVEL = getLogLevel(import.meta.env.VITE_LOG_DB_LEVEL, 2) // Default to info level for database

// Simple logger class
export class Logger {
  private name: string
  private dbEnabled: boolean

  constructor(name: string = 'Logger', db: boolean = true) {
    this.name = name
    this.dbEnabled = db
  }

  private shouldLogToConsole(level: LogLevel): boolean {
    const levels = { debug: 0, info: 2, warn: 3, error: 4 }
    return levels[level] >= CONSOLE_LOG_LEVEL
  }

  private shouldLogToDatabase(level: LogLevel): boolean {
    const levels = { debug: 0, info: 2, warn: 3, error: 4 }
    return levels[level] >= DB_LOG_LEVEL
  }

  private formatMessage(level: LogLevel, name: string): string {
    const timestamp = new Date().toISOString()
    const levelStr = level.toUpperCase().padEnd(5)
    return `[${timestamp}] ${levelStr} ${this.name}:${name}`
  }

  private async writeToFirestore(
    level: LogLevel,
    message: string,
    meta?: LogContext & Record<string, unknown>,
  ) {
    if (!FS_ENABLED || !this.dbEnabled) return

    try {
      const payload = {
        ts: Date.now(),
        level,
        name: this.name,
        message: message,
        context: typeof meta === 'object' ? meta : undefined,
      }

      const col = collection(db, 'app_logs')
      await addDoc(col, convertToFirestore(payload))
    } catch (e) {
      console.warn('Failed to write log to Firestore', e)
    }
  }

  private log(level: LogLevel, name: string, meta?: LogContext & Record<string, unknown>) {
    const message = this.formatMessage(level, name)

    // Console output
    if (CONSOLE_ENABLED && this.shouldLogToConsole(level)) {
      switch (level) {
        case 'debug':
          console.debug(message, meta)
          break
        case 'info':
          console.info(message, meta)
          break
        case 'warn':
          console.warn(message, meta)
          break
        case 'error':
          console.error(message, meta)
          break
      }
    }

    // Firestore output (async, don't await to avoid blocking)
    if (this.shouldLogToDatabase(level)) {
      this.writeToFirestore(level, name, meta)
    }
  }

  debug(name: string, meta?: LogContext & Record<string, unknown>) {
    this.log('debug', name, meta)
  }

  info(name: string, meta?: LogContext & Record<string, unknown>) {
    this.log('info', name, meta)
  }

  warn(name: string, meta?: LogContext & Record<string, unknown>) {
    this.log('warn', name, meta)
  }

  error(name: string, meta?: (LogContext & { error?: unknown }) | string) {
    this.log('error', name, typeof meta === 'string' ? { message: meta } : meta)
  }

  child(suffix: string) {
    return new Logger(`${this.name}:${suffix}`)
  }
}

// Export singleton instance
export const logger = new Logger()
